import { useEffect, useMemo, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import { StreamChat } from "stream-chat"

import { StreamType } from "@bullseye/types"

import { useUserContext } from "../../UserAuthProvider/UserProvider"

const STREAM_TOKEN_POLL_MS = 3_000
const STREAM_TOKEN_MAX_ATTEMPTS = 10

export const useClient = (apiKey: string) => {
  const { user, refetch } = useUserContext()

  const client = useMemo(
    () => StreamChat.getInstance<StreamType>(apiKey),
    [apiKey],
  )

  // After signup the cached user may lack a stream_token because useMe()
  // resolved before the backend finished creating the Stream identity.
  // Poll /me until the token appears so connectUser can proceed.
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch
  const attemptsRef = useRef(0)

  useEffect(() => {
    if (user.stream_token) {
      attemptsRef.current = 0
      return
    }

    const interval = setInterval(() => {
      if (attemptsRef.current >= STREAM_TOKEN_MAX_ATTEMPTS) {
        clearInterval(interval)
        return
      }
      attemptsRef.current += 1
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      void refetchRef.current().catch(() => {})
    }, STREAM_TOKEN_POLL_MS)

    return () => clearInterval(interval)
  }, [user.stream_token])

  const { isLoading, error } = useQuery(
    ["client", apiKey, user.stream_token],
    () => {
      return client.connectUser(
        {
          id: user.stream_id,
          name: user.formatted_name || user.first_name,
          image: user?.avatar?.url,
        },
        user.stream_token,
      )
    },
    {
      enabled: !!client && !!user.stream_token,
      staleTime: Infinity, // only connect the user once
      cacheTime: Infinity, // never clear the client once we have one
    },
  )

  return {
    client,
    isLoading,
    error,
  }
}
